import { isBefore, isSameDay, isWithinInterval, startOfDay, subDays } from 'date-fns';

/**
 * Estado de SLA de uma tarefa. O "SLA" do MVP é o próprio prazo (`due_at`):
 * - `done`: concluída ou cancelada — saiu do radar.
 * - `overdue`: tem prazo no passado e não foi concluída → estoura o SLA (vermelho).
 * - `due_today`: vence hoje — exige atenção.
 * - `on_track`: prazo futuro ou sem prazo.
 */
export type TaskSlaStatus = 'done' | 'overdue' | 'due_today' | 'on_track';

interface TaskLike {
  due_at: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  completed_at?: string | null;
  assigned_to_user_id?: string | null;
}

export function taskSlaStatus(task: TaskLike, now: Date = new Date()): TaskSlaStatus {
  if (task.status === 'completed' || task.status === 'cancelled') return 'done';
  if (!task.due_at) return 'on_track';
  const due = new Date(task.due_at);
  // SLA é por dia: vencer hoje (mesmo de manhã) não conta como atraso.
  if (isSameDay(due, now)) return 'due_today';
  if (isBefore(due, startOfDay(now))) return 'overdue';
  return 'on_track';
}

export interface TaskMetrics {
  /** Tarefas abertas com prazo estourado. */
  overdue: number;
  /** Tarefas abertas que vencem hoje. */
  dueToday: number;
  /** Tarefas em andamento (status in_progress). */
  inProgress: number;
  /** Tarefas abertas no total (pending + in_progress). */
  open: number;
  /** Tarefas concluídas nos últimos 7 dias. */
  completedLast7d: number;
}

export function computeTaskMetrics<T extends TaskLike>(
  tasks: T[],
  now: Date = new Date(),
): TaskMetrics {
  const m: TaskMetrics = { overdue: 0, dueToday: 0, inProgress: 0, open: 0, completedLast7d: 0 };
  const weekAgo = subDays(now, 7);
  for (const t of tasks) {
    if (t.status === 'completed') {
      if (t.completed_at && isWithinInterval(new Date(t.completed_at), { start: weekAgo, end: now })) {
        m.completedLast7d += 1;
      }
      continue;
    }
    if (t.status === 'cancelled') continue;
    m.open += 1;
    if (t.status === 'in_progress') m.inProgress += 1;
    const sla = taskSlaStatus(t, now);
    if (sla === 'overdue') m.overdue += 1;
    else if (sla === 'due_today') m.dueToday += 1;
  }
  return m;
}

export interface AssigneeTaskSummary {
  assigneeUserId: string | null;
  overdue: number;
  dueToday: number;
  open: number;
  completedLast7d: number;
}

/**
 * Agrupa as métricas por responsável (`assigned_to_user_id`). Tarefas sem
 * responsável caem no balde de chave `null`. Usado no dashboard do gerente.
 */
export function summarizeByAssignee<T extends TaskLike>(
  tasks: T[],
  now: Date = new Date(),
): AssigneeTaskSummary[] {
  const byKey = new Map<string, AssigneeTaskSummary>();
  const keyFor = (id: string | null | undefined) => id ?? '__none__';

  for (const t of tasks) {
    const id = t.assigned_to_user_id ?? null;
    const key = keyFor(id);
    let entry = byKey.get(key);
    if (!entry) {
      entry = { assigneeUserId: id, overdue: 0, dueToday: 0, open: 0, completedLast7d: 0 };
      byKey.set(key, entry);
    }
    const metrics = computeTaskMetrics([t], now);
    entry.overdue += metrics.overdue;
    entry.dueToday += metrics.dueToday;
    entry.open += metrics.open;
    entry.completedLast7d += metrics.completedLast7d;
  }

  // Ordena: mais atrasadas primeiro, depois mais abertas.
  return [...byKey.values()].sort(
    (a, b) => b.overdue - a.overdue || b.open - a.open,
  );
}
